import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { ISODate, Locale } from '../../shared/types'
import { LOCALES } from '../../shared/types'
import type { Dict } from './dict'
import { en } from './en'
import { zh } from './zh'

/**
 * 界面语言。
 *
 * 中文那份是母版，其余语言以 `Dict` 为类型 —— 漏一个 key 是编译错误，
 * 不会等到用户点进某个页面才发现半中半英。
 *
 * 再加一门语言的完整步骤：
 *   1. 照着 zh.ts 写一份 es.ts（`const es: Dict = {...}`）
 *   2. 在下面的 DICTS 里加一行
 *   3. 在 shared/types.ts 的 LOCALES 里加上代码（服务端的报错和邮件也认这个）
 *   4. 在 worker/i18n.ts 里补一份同名的报错表，mail.ts 里补一份邮件文案
 * 别的地方都不用动。
 */

const DICTS: Record<Locale, Dict> = { zh, en }

export type { Dict, Locale }
export { LOCALES }

const STORAGE_KEY = 'moonly.locale'

// ── 选哪门语言 ──────────────────────────────────────────────────────────

function parse(v: unknown): Locale | null {
  if (typeof v !== 'string') return null
  // 'zh-CN' / 'en-GB' 都只看第一段
  const primary = v.toLowerCase().split('-')[0] as Locale
  return LOCALES.includes(primary) ? primary : null
}

/** 用户在这台设备上明确选过语言吗。没选过才跟着账号或浏览器走。 */
export function storedLocale(): Locale | null {
  try {
    return parse(localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    /* 隐私模式下 localStorage 可能不可用，忽略即可 */
  }
}

/** 浏览器语言。都对不上时落到英文 —— 看不懂的英文界面也比看不懂的中文界面通用。 */
function fromBrowser(): Locale {
  const tags = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])
  for (const tag of tags) {
    const hit = parse(tag)
    if (hit) return hit
  }
  return 'en'
}

// ── 给非组件代码用的读取口 ──────────────────────────────────────────────
//
// api.ts 在 React 之外抛错误、拼请求头，也需要知道当前语言。
// 这个模块级变量由下面的 Provider 保持同步，是只读的镜像，不要在别处赋值。

let current: Locale = 'en'

export function getLocale(): Locale {
  return current
}

/** 某门语言用它自己的说法叫什么。语言切换器里显示的就是这个，不翻译。 */
export function localeName(locale: Locale): string {
  return DICTS[locale].languageName
}

/** 组件外取文案（api.ts 的网络错误之类）。组件里请用 useI18n。 */
export function getDict(): Dict {
  return DICTS[current]
}

function applyLocale(locale: Locale): void {
  current = locale
  if (typeof document !== 'undefined') {
    document.documentElement.lang = DICTS[locale].meta.tag
  }
}

// 模块加载时先定一个，让首屏之前发生的事（比如 api 请求头）也有值可用
applyLocale(storedLocale() ?? fromBrowser())

// ── 日期格式 ────────────────────────────────────────────────────────────
//
// ISO 字符串一律当 UTC 正午来格式化。用本地时区的话，同一个 '2026-08-20'
// 在东八区和美西会显示成不同的两天。

const cache = new Map<string, Intl.DateTimeFormat>()

function formatter(tag: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tag}:${JSON.stringify(opts)}`
  let f = cache.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(tag, { ...opts, timeZone: 'UTC' })
    cache.set(key, f)
  }
  return f
}

function toDate(iso: ISODate): Date {
  return new Date(`${iso}T12:00:00Z`)
}

/** 8月20日 / Aug 20 */
export function formatDateIn(locale: Locale, iso: ISODate): string {
  return formatter(DICTS[locale].meta.tag, { month: 'short', day: 'numeric' }).format(toDate(iso))
}

/** 8/20 —— 图表刻度这种地方用 */
export function formatShortIn(locale: Locale, iso: ISODate): string {
  return formatter(DICTS[locale].meta.tag, { month: 'numeric', day: 'numeric' }).format(toDate(iso))
}

// ── React 侧 ────────────────────────────────────────────────────────────

export interface I18n {
  locale: Locale
  t: Dict
  /** 用户在设置里选了语言：记在这台设备上，之后由 App 同步给服务端 */
  setLocale(locale: Locale): void
  /** 账号上存着的语言。仅在这台设备没有明确选择时才采纳 */
  adoptRemote(locale: Locale): void
  formatDate(iso: ISODate): string
  formatShort(iso: ISODate): string
}

const I18nContext = createContext<I18n | null>(null)

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => storedLocale() ?? fromBrowser())

  useEffect(() => {
    applyLocale(locale)
  }, [locale])

  const setLocale = useCallback((next: Locale) => {
    rememberLocale(next)
    applyLocale(next)
    setLocaleState(next)
  }, [])

  const adoptRemote = useCallback((next: Locale) => {
    // 这台设备上选过就以设备为准：人在这儿看着屏幕，他的选择比账号里那份新
    if (storedLocale()) return
    applyLocale(next)
    setLocaleState(next)
  }, [])

  const value = useMemo<I18n>(
    () => ({
      locale,
      t: DICTS[locale],
      setLocale,
      adoptRemote,
      formatDate: (iso) => formatDateIn(locale, iso),
      formatShort: (iso) => formatShortIn(locale, iso),
    }),
    [locale, setLocale, adoptRemote],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const v = useContext(I18nContext)
  if (!v) throw new Error('useI18n must be used inside I18nProvider')
  return v
}
